import { Component, OnDestroy, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Helps } from 'src/app/libs/helps';
import { ApiService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { GlobalPanicAlertService } from 'src/app/services/global-panic-alert.service';
import { CentralistModeService } from 'src/app/services/centralist-mode.service';
import { CentralistModeModalComponent } from 'src/app/shared/components/centralist-mode-modal/centralist-mode-modal.component';
import { environment } from 'src/environments/environment';
import { formatRut, RutFormat } from '@fdograph/rut-utilities';

declare const grecaptcha: any;

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
})
export class LoginComponent implements OnInit, AfterViewInit {
  recaptchaSiteKey: string = environment.recaptchaSiteKey;
  recaptchaWidgetId: number | null = null;

  constructor(
    private auth: AuthService,
    private router: Router,
    private api: ApiService,
    private toast: ToastrService,
    private helps: Helps,
    private globalPanicAlertService: GlobalPanicAlertService,
    private centralistModeService: CentralistModeService
  ) {

  }

  ngOnInit(): void {
    if (this.auth.isLogged) {
      this.router.navigate(['/control-ingreso/registro/gestion']);
    }
    (window as any).resolved = this.resolved.bind(this);
  }

  ngAfterViewInit(): void {
    this.loadRecaptcha();
  }

  loadRecaptcha(): void {
    if (typeof grecaptcha !== 'undefined' && grecaptcha.render) {
      this.renderRecaptcha();
    } else {
      // Wait for grecaptcha to load
      setTimeout(() => this.loadRecaptcha(), 100);
    }
  }

  renderRecaptcha(): void {
    const recaptchaElement = document.querySelector('.g-recaptcha');
    if (recaptchaElement && !this.recaptchaWidgetId) {
      this.recaptchaWidgetId = grecaptcha.render(recaptchaElement, {
        'sitekey': this.recaptchaSiteKey,
        'callback': this.resolved.bind(this),
        'expired-callback': this.expiredCallback.bind(this),
        'error-callback': this.errorCallback.bind(this)
      });
    }
  }

  loginForm = new UntypedFormGroup({
    rut: new UntypedFormControl(''),
    email: new UntypedFormControl(''),
    pass: new UntypedFormControl(''),
    captcha: new UntypedFormControl(''),
    fcm:new UntypedFormControl('')
  });

  login() {
    // Validate captcha before submitting
    const captchaValue = this.loginForm.get('captcha')?.value;
    if (!captchaValue) {
      this.toast.warning('Por favor, complete el captcha antes de continuar', 'Captcha requerido');
      return;
    }
    
    // Validate required fields
    if (!this.loginForm.get('rut')?.value) {
      this.toast.warning('Por favor, ingrese su RUT', 'Campo requerido');
      return;
    }
    
    if (!this.loginForm.get('pass')?.value) {
      this.toast.warning('Por favor, ingrese su contraseña', 'Campo requerido');
      return;
    }
    
    //let data = this.helps?.getTokenFcm();
    this.api.login(this.loginForm).subscribe(
      (response) => {
        this.helps.saveToken(response);
        console.log("LOGIN:",response);
        this.toast.success("Bienvenido " + response.data['name']);

        // Initialize global panic alert service after successful login
        this.globalPanicAlertService.initializeAfterLogin();

        const level = Number(response.level);

        // ADMINISTRADOR (level 99) → Modal de selección entre central o admin panel
        if (level === 99) {
          this.router.navigate(['/modo-centralista']);
        }
        // SUPERVISOR (level 2) → Dashboard supervisor
        else if (level === 2) {
          this.router.navigate(['/supervisor']);
        }
        // CENTRALISTA (level 50) → Central de Seguridad directamente
        else if (level === 50) {
          this.router.navigate(['/central-de-seguridad']);
        }
        // Otros niveles no tienen acceso a esta app
        else {
          this.router.navigate(['/login']);
        }
      },
      (e) => {
        this.toast.warning(e.error.mistakes, '¡Tenemos un error!');
        this.resetRecaptcha();
        // Clear the captcha form control to force user to complete it again
        this.loginForm.get('captcha')?.setValue('');
      }
    );
  }

  onFormatRut(value: any) {
    if (value.target.value.length > 8) {
      this.loginForm
        .get('rut')
        ?.setValue(formatRut(value.target.value, RutFormat.DOTS_DASH));
    }
  }

  resolved(captchaResponse: string) {
    this.loginForm.get('captcha')?.setValue(captchaResponse);
  }

  expiredCallback(): void {
    this.loginForm.get('captcha')?.setValue('');
    this.toast.warning('El captcha ha expirado. Por favor, inténtalo de nuevo.', 'Captcha expirado');
  }

  errorCallback(): void {
    this.loginForm.get('captcha')?.setValue('');
    this.toast.error('Error al cargar el captcha. Por favor, recarga la página.', 'Error de captcha');
  }

  resetRecaptcha(): void {
    if (typeof grecaptcha !== 'undefined' && this.recaptchaWidgetId !== null) {
      grecaptcha.reset(this.recaptchaWidgetId);
    }
  }
}
